import { isDefined } from '@sama/js-core';
import { AnnotationTransientState } from '../../cross-team-shared/annotation/shared/state/annotation-state';
import {
  ShapeFilter,
  ViewMode,
} from '../../cross-team-shared/state/abstract-sensor-content-state';
import { CalibrationOptions } from '../../cross-team-shared/state/calibration-options-state';
import { EnhancedCloudSettingsState } from '../../cross-team-shared/state/enhanced-point-cloud-setting-state';
import { LayerSettingsState3D } from '../../cross-team-shared/state/layer-settings-state-3d';
import { EditMode3D } from '../../cross-team-shared/types/tool-modes';
import { LutData } from '../../team-apis/core/lut-data';
import {
  Point2D,
  Point3D,
} from '../../team-apis/otterdam/answers/shape-typings';
import { CuboidFace } from '../render/mesh/cuboid/cuboid-helper';
import { Axis } from '../renderers/side-view/side-view.component';
import { ObjectWith } from '../utils/misc-utils';
import { CuboidState3D } from './cuboid-state-3d';
import { LineState3D } from './line-state-3d';
import { PolygonState3D } from './polygon-state-3d';
import { ProjectionSettings } from './projection-settings';

export interface EditSource {
  scene: 'side view' | 'main view';
  index?: Axis;
}

export type MeasurementMode =
  | 'free-point'
  | 'cuboid-to-sensor'
  | 'subpoint-height';

export interface FreePointMeasurement {
  reference: Point3D;
  active: boolean;
}

export interface SubpointHeightMeasurement extends FreePointMeasurement {
  target: Point3D;
}

interface TrainingShapes {
  cuboids: StrictRecord<number, CuboidState3D>;
  lines: StrictRecord<number, LineState3D>;
  polygons: StrictRecord<number, PolygonState3D>;
}

export class RenderState {
  calibrationOptions: ObjectWith<CalibrationOptions> = {};
  containerSize: StrictRecord<string, Point2D> = {};
  cuboids: ObjectWith<CuboidState3D> = {};
  currentEditSource?: EditSource;
  debuggerGroundingClosestPoints?: Uint8ClampedArray = undefined;
  editMode: EditMode3D = 'translate';
  enhancedCloudSettings = new EnhancedCloudSettingsState();
  frameNumber = 0;
  hideGround = false;
  highlightedShapeIndex?: number;
  highlightedFace?: CuboidFace;
  inProgressCuboid?: CuboidState3D;
  inProgressArrowCuboid?: {
    reference: Point3D;
    target: Point3D;
  };
  layerSettings!: LayerSettingsState3D;
  lines: ObjectWith<LineState3D> = {};
  lutData?: LutData;
  measurementMode?: MeasurementMode;
  freePointMeasurement?: FreePointMeasurement;
  polygons: ObjectWith<PolygonState3D> = {};
  projectionSettings = new ProjectionSettings();
  selection: ObjectWith<true> = {};
  shapeFilters: ShapeFilter[] = [];
  shapeTransientInfo: StrictRecord<number, AnnotationTransientState> = {};
  showRoute = true;
  subpointHeightMeasurement?: SubpointHeightMeasurement;
  trainingShapes: TrainingShapes = {
    cuboids: {},
    lines: {},
    polygons: {},
  };
  viewMode: ViewMode = 'all';
}

/** Use this function when deleting members of the RenderState,
 * so the store isn't triggered in vain by using store.delete() */
export function deleteIfDefined<T>(value: T) {
  return isDefined(value) ? undefined : value;
}
